import React from "react";
import Loader from "../../../Loader/Loader";
import { useEvent } from "../../../../context/EventContext";
import DatePicker from "react-datepicker";
import Select from "react-select";
import UserDetails from "./UserDetails";

// * This component renders a form to input meeting details such as meeting
// * agenda, location, start and end dates, and invitees. It also displays a
// * table of invitees and their statuses.
const MeetingForm = ({
  locations,
  startDate,
  endDate,
  startTime,
  endTime,
  userOptions,
  inputValue,
  setInputValue,
  meetingFormDetails,
  handleConnection,
  meetingEditIndex,
  handleSave,
  handleUpdate,
  handleDelete,
  modalVisible,
  customStyles,
  handleClose,
  selectedUser,
  connStatus,
  handleConnect,
}) => {
  const { meetingFormData, setMeetingFormData, loading } = useEvent();

  //   * Handles changes to meeting form data. Updates meetingFormData with the
  //   * changed value. Used for input fields like meeting title, description, and
  //   * number of attendees.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setMeetingFormData({
      ...meetingFormData,
      [name]: value,
    });
  };

  //   * Handles the change event of the location dropdown in the meeting form.
  //   * Updates the meetingFormData with the selected locationID.
  const handleLocationChange = (e) => {
    const selectedLocation = e.target.value;
    setMeetingFormData({
      ...meetingFormData,
      locationID: selectedLocation,
    });
  };

  
  const customStyle = {
    // Hide the dropdown arrow
    dropdownIndicator: () => ({
      display: "none",
    }),
  };

  //   * Handles the change event for selecting invitees in the meeting form.
  //   * Updates the meetingFormData with the array of selected userIDs.
  const handleInviteChange = (selectedUsers) => {
    const inviteeValues = selectedUsers
      ? selectedUsers.map((user) => user.value) // Extract userID from selected users
      : [];
    setMeetingFormData({
      ...meetingFormData,
      invited: inviteeValues, // Store the array of userIDs
    });
  };

  return (
    <div className="meeting-details">
      <h1>Meeting Details</h1>
      <label htmlFor="meeting-agenda">Meeting Agenda</label>
      <input
        type="text"
        id="meeting-agenda"
        name="agenda"
        value={meetingFormData?.agenda || ""}
        onChange={handleChange}
        placeholder="Meeting Agenda"
      />
      <label htmlFor="meeting-location">Meeting Location</label>
      <select
        onChange={handleLocationChange}
        value={meetingFormData?.locationID}
      >
        <option value="select">Select Location</option>
        {locations.map((location) => (
          <option key={location._id} value={location._id}>
            {location.space_name}
          </option>
        ))}
      </select>

      {/* Start Date and Time */}
      <div className="date-section">
        <div className="start-date-time">
          <label htmlFor="start_date" className="date-picker-label">
            Start <span className="//required">*</span>
          </label>
          <div className="date-picker-wrapper">
            <DatePicker
              name="start"
              id="start-date"
              showIcon
              dateFormat="dd/MM/yyyy hh:mm aa" // Adjust date and time format as needed
              placeholderText="DD/MM/YY HH:MM AM/PM"
              className="date-picker-input"
              selected={
                meetingFormData.start instanceof Date
                  ? meetingFormData.start
                  : new Date()
              }
              onChange={(date) => {
                if (date instanceof Date && !isNaN(date)) {
                  setMeetingFormData((prevData) => ({
                    ...prevData,
                    start: date, // Ensure it's a valid Date object
                  }));
                } else {
                  console.error("Invalid date selected");
                }
              }}
              minDate={startDate ? new Date(startDate) : null} // Ensure startDate is a Date object
              maxDate={endDate ? new Date(endDate) : null} // Ensure endDate is a Date object
              minTime={new Date(startTime)} // Convert to Date object
              maxTime={new Date(endTime)} // Convert to Date object
              showTimeSelect // Enable time selection
              timeIntervals={30} // Time intervals (30 minutes in this case)
              timeCaption="Time" // Caption for the time selection dropdown
            />
          </div>
        </div>

        {/* End Date and Time */}
        <div className="start-date-time">
          <label htmlFor="start_date" className="date-picker-label">
            End <span className="//required">*</span>
          </label>
          <div className="date-picker-wrapper">
            <DatePicker
              name="end"
              id="start-date"
              showIcon
              dateFormat="dd/MM/yyyy hh:mm aa" // Adjust date and time format as needed
              placeholderText="DD/MM/YY HH:MM AM/PM"
              className="date-picker-input"
              selected={
                meetingFormData.end instanceof Date
                  ? meetingFormData.end
                  : new Date()
              }
              onChange={(date) => {
                if (date instanceof Date && !isNaN(date)) {
                  setMeetingFormData((prevData) => ({
                    ...prevData,
                    end: date,
                  }));
                } else {
                  console.log("Invalid date selected");
                }
              }}
              minDate={startDate ? new Date(startDate) : null} // Ensure startDate is a Date object
              maxDate={endDate ? new Date(endDate) : null} // Ensure endDate is a Date object
              minTime={new Date(startTime)} // Convert to Date object
              maxTime={new Date(endTime)} // Convert to Date object
              showTimeSelect // Enable time selection
              timeIntervals={30} // Time intervals (30 minutes in this case)
              timeCaption="Time" // Caption for the time selection dropdown
            />
          </div>
        </div>
      </div>
      <label htmlFor="invite">Invite</label>
      <Select
        isMulti
        isSearchable
        isClearable={false}
        styles={customStyle}
        name="invited"
        options={userOptions}
        value={
          Array.isArray(meetingFormData?.invited)
            ? meetingFormData.invited.map((userID) =>
                userOptions.find((option) => option.value === userID)
              )
            : []
        }
        onChange={(selectedUsers) => handleInviteChange(selectedUsers)}
        inputValue={inputValue} // Add inputValue state to track user input
        onInputChange={(value) => setInputValue(value)} // Update input value on search
        menuIsOpen={inputValue.length > 0} // Only show menu when input is not empty
      />

      {/* Invitees Table */}
      <div className="invitees-table">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {/* First, render users from the invited array who are not in the attendee array */}
            {meetingFormData?.invited
              .filter(
                (userID) =>
                  !meetingFormDetails?.some((meeting) =>
                    meeting.attendee.includes(userID)
                  )
              ) // Filter out users who are in the attendee list
              .map((userID) => {
                const userDetail = userOptions.find(
                  (option) => option.value === userID
                );

                if (!userDetail) return null; // Handle case where user not found
                return (
                  <tr key={userID}>
                    <td
                      style={{ cursor: "pointer" }}
                      onClick={() => handleConnection(userDetail)}
                    >
                      {userDetail.label}
                    </td>
                    <td>
                      <button className="add-space">Resend</button>
                    </td>
                  </tr>
                );
              })}

            {/* Now, render users from the attendee array */}
            {meetingFormDetails?.map((meeting) =>
              meeting.attendee.map((userID) => {
                const user = userOptions.find(
                  (option) => option.value === userID
                );
                return (
                  <tr key={userID}>
                    <td>{user?.label}</td>
                    <td>
                      <p>Invitation Accepted</p>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <UserDetails
        modalVisible={modalVisible}
        customStyles={customStyles}
        handleClose={handleClose}
        selectedUser={selectedUser}
        connStatus={connStatus}
        handleConnect={handleConnect}
      />

      {meetingEditIndex === null ? (
        <button onClick={(e) => handleSave(e)} className="add-space">
          {loading ? <Loader /> : "SAVE"}
        </button>
      ) : (
        <>
          <button onClick={handleUpdate} className="create-button">
            {loading ? <Loader /> : "UPDATE"}
          </button>
          <button
            onClick={(e) => handleDelete(e, meetingFormData?._id)}
            className="create-button"
          >
            {loading ? <Loader /> : "DELETE"}
          </button>
        </>
      )}
    </div>
  );
};

export default MeetingForm;
